import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UserPlus, Users, Shield, User, Briefcase, Trash2, Pencil, Key, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagement = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, email: null, name: null });
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [passwordDialog, setPasswordDialog] = useState({ open: false, email: null, name: null });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'agent',
    agent_name: '',
  });
  const [editFormData, setEditFormData] = useState({
    agent_name: '',
    email: '',
    role: '',
    joining_date: '',
    last_working_date: '',
  });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/auth/register`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('User created successfully!');
      setOpen(false);
      setFormData({ email: '', password: '', role: 'agent', agent_name: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/users/${deleteDialog.email}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('User deleted successfully!');
      setDeleteDialog({ open: false, email: null, name: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditDialog = (u) => {
    setEditFormData({
      agent_name: u.agent_name,
      email: u.email,
      role: u.role,
      joining_date: u.joining_date || '',
      last_working_date: u.last_working_date || '',
    });
    setEditDialog({ open: true, user: u });
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const originalEmail = editDialog.user.email;
      
      await axios.put(`${BACKEND_URL}/api/users/${originalEmail}`, {
        agent_name: editFormData.agent_name,
        email: editFormData.email !== originalEmail ? editFormData.email : undefined,
        role: editFormData.role,
        joining_date: editFormData.joining_date || null,
        last_working_date: editFormData.last_working_date || null,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success('User updated successfully!');
      setEditDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const openPasswordDialog = (u) => {
    setNewPassword('');
    setConfirmPassword('');
    setPasswordDialog({ open: true, email: u.email, name: u.agent_name });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/users/${passwordDialog.email}/change-password`, {
        email: passwordDialog.email,
        new_password: newPassword,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success('Password changed successfully!');
      setPasswordDialog({ open: false, email: null, name: null });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const getRoleIcon = (role) => {
    if (role === 'admin') return <Shield className="w-4 h-4 text-purple-600" />;
    if (role === 'manager') return <Briefcase className="w-4 h-4 text-blue-600" />;
    return <User className="w-4 h-4 text-emerald-600" />;
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') return 'bg-purple-100 text-purple-800';
    if (role === 'manager') return 'bg-blue-100 text-blue-800';
    return 'bg-emerald-100 text-emerald-800';
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-8 overflow-auto" data-testid="user-management">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-purple-50 p-3 rounded-xl">
                <Users className="w-6 h-6 text-purple-600" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-4xl font-bold tracking-tight text-primary">User Management</h1>
                <p className="text-muted-foreground">Manage admin, manager, and agent accounts</p>
              </div>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-user-button" className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Create User
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New User</DialogTitle>
                  <DialogDescription>Add a new admin, manager, or agent to the system</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="agent_name">Full Name</Label>
                    <Input
                      id="agent_name"
                      placeholder="John Doe"
                      value={formData.agent_name}
                      onChange={(e) => setFormData({ ...formData, agent_name: e.target.value })}
                      required
                      data-testid="user-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="user@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="user-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      data-testid="user-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                      <SelectTrigger data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agent">Agent (View & Search Only)</SelectItem>
                        <SelectItem value="manager">Manager (Upload & Search Data)</SelectItem>
                        <SelectItem value="admin">Admin (Full Access)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-user-button">
                    Create User
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-white">
              <CardTitle>All Users</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-50/50">
                    <TableRow>
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Role</TableHead>
                      <TableHead className="font-semibold">Joining</TableHead>
                      <TableHead className="font-semibold">Last Date</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          Loading users...
                        </TableCell>
                      </TableRow>
                    ) : users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((u, index) => (
                        <TableRow key={u.email} className={`hover:bg-slate-50/80 transition-colors ${u.is_active === false ? 'opacity-50' : ''}`} data-testid={`user-row-${index}`}>
                          <TableCell className="font-medium">{u.agent_name}</TableCell>
                          <TableCell className="font-mono text-xs">{u.email}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getRoleIcon(u.role)}
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadge(u.role)}`}>
                                {u.role}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs">{u.joining_date || '—'}</TableCell>
                          <TableCell className="text-xs">{u.last_working_date || '—'}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              u.is_active === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {u.is_active === false ? 'Inactive' : 'Active'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              {/* Edit Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(u)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                data-testid={`edit-user-${index}`}
                                title="Edit User"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              {/* Change Password Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPasswordDialog(u)}
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                data-testid={`password-user-${index}`}
                                title="Change Password"
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              {/* Delete Button - Not for current user */}
                              {u.email !== user.email && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteDialog({ open: true, email: u.email, name: u.agent_name })}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-user-${index}`}
                                  title="Delete User"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </main>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold">{deleteDialog.name}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              Edit User
            </DialogTitle>
            <DialogDescription>Update user information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="edit_agent_name">Full Name</Label>
              <Input
                id="edit_agent_name"
                placeholder="John Doe"
                value={editFormData.agent_name}
                onChange={(e) => setEditFormData({ ...editFormData, agent_name: e.target.value })}
                required
                data-testid="edit-user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                placeholder="user@example.com"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
                data-testid="edit-user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_role">Role</Label>
              <Select 
                value={editFormData.role} 
                onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}
                disabled={editDialog.user?.email === user.email}
              >
                <SelectTrigger data-testid="edit-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agent (View & Search Only)</SelectItem>
                  <SelectItem value="manager">Manager (Upload & Search Data)</SelectItem>
                  <SelectItem value="admin">Admin (Full Access)</SelectItem>
                </SelectContent>
              </Select>
              {editDialog.user?.email === user.email && (
                <p className="text-xs text-muted-foreground">You cannot change your own role</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit_joining_date">Joining Date</Label>
                <Input
                  id="edit_joining_date"
                  placeholder="DD/MM/YYYY"
                  value={editFormData.joining_date}
                  onChange={(e) => setEditFormData({ ...editFormData, joining_date: e.target.value })}
                  data-testid="edit-joining-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_last_working_date">Last Working Date</Label>
                <Input
                  id="edit_last_working_date"
                  placeholder="DD/MM/YYYY"
                  value={editFormData.last_working_date}
                  onChange={(e) => setEditFormData({ ...editFormData, last_working_date: e.target.value })}
                  data-testid="edit-last-working-date"
                />
                <p className="text-xs text-muted-foreground">If set to past date, account will be deactivated</p>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setEditDialog({ open: false, user: null })}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-user-button">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialog.open} onOpenChange={(open) => setPasswordDialog({ ...passwordDialog, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <span className="font-semibold">{passwordDialog.name}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <Input
                id="confirm_password"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setPasswordDialog({ open: false, email: null, name: null });
                  setNewPassword('');
                  setConfirmPassword('');
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-amber-600 hover:bg-amber-700" 
                data-testid="change-password-button"
                disabled={!newPassword || newPassword !== confirmPassword}
              >
                Change Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
